import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Radar, Target, TrendingUp, AlertTriangle, Eye, Clock, FileText,
  Activity, Search, Users, DollarSign, ArrowRight, ExternalLink,
  Plus, RefreshCw, BarChart3, PieChart, LineChart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { StatusIndicator } from '@/components/ui/status-indicator';

const statCards = [
  { key: 'competitorsTracked', label: 'Competitors Tracked', icon: Users },
  { key: 'adsAnalyzed', label: 'Ads Analyzed', icon: Eye },
  { key: 'revenueOpportunity', label: 'Revenue Opportunity', icon: DollarSign, prefix: '$', suffix: 'k' },
  { key: 'alertsActive', label: 'Active Alerts', icon: AlertTriangle },
  { key: 'campaignsRunning', label: 'Campaigns Running', icon: Target },
  { key: 'performanceScore', label: 'Performance Score', icon: TrendingUp, suffix: '/100' },
];


function getActivityIcon(type) {
  switch (type) {
    case 'new_competitor': return <Users className="w-3.5 h-3.5 text-status-warning" />;
    case 'scaling_detected': return <TrendingUp className="w-3.5 h-3.5 text-status-active" />;
    case 'trend_opportunity': return <BarChart3 className="w-3.5 h-3.5 text-primary" />;
    default: return <Activity className="w-3.5 h-3.5 text-muted-foreground" />;
  }
}

function getActivityBadge(type) {
  switch (type) {
    case 'new_competitor': return <Badge variant="warning" className="text-[10px]">NEW COMPETITOR</Badge>;
    case 'scaling_detected': return <Badge variant="success" className="text-[10px]">SCALING</Badge>;
    case 'trend_opportunity': return <Badge variant="secondary" className="text-[10px]">OPPORTUNITY</Badge>;
    default: return <Badge variant="secondary" className="text-[10px]">ALERT</Badge>;
  }
}

function formatTimeAgo(date) {
  try {
    const now = new Date();
    const targetDate = new Date(date);

    // Check if date is valid
    if (isNaN(targetDate.getTime())) {
      return 'unknown';
    }

    const diffMs = now - targetDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return targetDate.toLocaleDateString();
  } catch (error) {
    console.warn('Invalid date in formatTimeAgo:', date, error);
    return 'unknown';
  }
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [competitorStatus, setCompetitorStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setError(null);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const fullUrl = backendUrl.startsWith('http') ? backendUrl : `https://${backendUrl}`;

      // Fetch alerts and competitors data in parallel
      const [alertsResponse, competitorsResponse] = await Promise.all([
        fetch(`${fullUrl}/api/alerts`),
        fetch(`${fullUrl}/api/competitors`)
      ]);

      const alertsData = await alertsResponse.json();
      const competitorsData = await competitorsResponse.json();

      if (alertsData.success) {
        setActivities(alertsData.data.alerts || []);
      }

      // Set competitor status for right sidebar
      if (competitorsData.success) {
        setCompetitorStatus(competitorsData.data.competitors.map(comp => ({
          id: comp.id,
          name: comp.name,
          adsActive: comp.adsActive,
          spendTrend: comp.spendTrend,
          status: comp.status
        })));
      }

      // Calculate comprehensive stats
      setStats({
        competitorsTracked: competitorsData.success ? competitorsData.data.totalTracked : 0,
        adsAnalyzed: competitorsData.success ? competitorsData.data.summary?.totalAdsTracked : 0,
        revenueOpportunity: 0,
        alertsActive: alertsData.success ? alertsData.data.alerts?.length : 0,
        campaignsRunning: 0,
        performanceScore: 0
      });

      setLoading(false);
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 radar-gradient rounded-xl flex items-center justify-center shadow-elevated">
              <Radar className="w-6 h-6 text-white" />
            </div>
            <div className="absolute -inset-1 radar-gradient rounded-xl opacity-20 blur animate-radar-pulse"></div>
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Intelligence Center</h1>
            <p className="text-sm text-muted-foreground mt-1">Real-time competitive intelligence dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" className="bg-cta hover:bg-cta/90 text-foreground transition-opacity" onClick={() => navigate('/dashboard/competitors')}>
            <Search className="w-3.5 h-3.5" />
            Analyze Competitor
          </Button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((card, i) => (
            <Card key={card.key} className="border-border/50 shadow-card hover:shadow-elevated transition-all duration-300 hover:border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <card.icon className="w-4 h-4 text-primary" />
                  </div>
                </div>
                <div className="font-heading text-2xl font-bold text-foreground">
                  {loading ? (
                    <div className="h-7 bg-muted rounded w-16 animate-pulse" />
                  ) : (
                    <>{card.prefix || ''}{stats?.[card.key] || 0}{card.suffix || ''}</>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{card.label}</div>
              </CardContent>
            </Card>
          ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Activity Feed */}
        <div className="lg:col-span-2">
          <Card className="border-border/50 shadow-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="font-heading text-base">Live Intelligence Feed</CardTitle>
                  <StatusIndicator variant="active" label="SCANNING" pulse />
                </div>
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/dashboard/alerts')}>View All</Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="flex gap-3">
                        <div className="w-8 h-8 rounded bg-muted animate-pulse" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-muted rounded w-3/4 animate-pulse" />
                          <div className="h-2 bg-muted rounded w-1/2 animate-pulse" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : error ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <AlertTriangle className="w-8 h-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-4">Failed to load activity data</p>
                    <Button variant="outline" size="sm" onClick={handleRefresh}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1" />
                      Retry
                    </Button>
                  </div>
                ) : activities.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <Activity className="w-8 h-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-2">No recent activity</p>
                    <p className="text-xs text-muted-foreground">Activity will appear here as competitors are detected</p>
                  </div>
                ) : (
                  <div className="space-y-0">
                    {activities.map((activity) => (
                      <div key={activity.id} className="flex gap-3 py-3 border-b border-border/50 last:border-0 animate-competitive-scan">
                        <div className="w-8 h-8 rounded-lg bg-secondary/70 flex items-center justify-center shrink-0">
                          {getActivityIcon(activity.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {getActivityBadge(activity.type)}
                            <span className="text-xs font-mono text-muted-foreground">{formatTimeAgo(activity.createdAt)}</span>
                          </div>
                          <p className="text-sm text-foreground">{activity.message || activity.title}</p>
                          <Button variant="link" size="sm" className="h-auto p-0 mt-1 text-xs" onClick={() => activity.actionUrl && navigate(activity.actionUrl)}>
                            View Analysis <ArrowRight className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Competitor Status */}
          <Card className="border-border/50 shadow-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base">Competitor Radar</CardTitle>
            </CardHeader>
            <CardContent>
              {competitorStatus.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Users className="w-8 h-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-2">No competitors tracked yet</p>
                  <p className="text-xs text-muted-foreground mb-4">Start by searching for competitors to track</p>
                  <Button size="sm" className="radar-gradient hover:opacity-90" onClick={() => navigate('/dashboard/competitors')}>
                    <Search className="w-3.5 h-3.5 mr-1" />
                    Search Competitors
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {competitorStatus.map((competitor) => (
                      <div key={competitor.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground truncate">{competitor.name}</div>
                          <div className="text-[10px] font-mono text-muted-foreground">
                            {competitor.adsActive} ads · {competitor.spendTrend} spend
                          </div>
                        </div>
                        <StatusIndicator
                          variant={competitor.status === 'scaling' ? 'warning' : 'active'}
                          label={competitor.status.toUpperCase()}
                          pulse={competitor.status === 'scaling'}
                        />
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate('/dashboard/competitors')}>
                    View All Competitors
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-border/50 shadow-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base">Intelligence Tools</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { label: 'Search Competitors', icon: Search, path: '/dashboard/competitors' },
                  { label: 'View Analytics', icon: BarChart3, path: '/dashboard/analytics' },
                  { label: 'Revenue Calculator', icon: DollarSign, path: '/dashboard/analytics/report' },
                  { label: 'Alert Settings', icon: AlertTriangle, path: '/dashboard/alerts' },
                ].map((action) => (
                  <Button
                    key={action.label}
                    variant="ghost"
                    className="w-full justify-between text-sm hover:bg-primary/5 transition-colors"
                    onClick={() => navigate(action.path)}
                  >
                    <span className="flex items-center gap-2">
                      <action.icon className="w-4 h-4" />
                      {action.label}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}